import React, { useEffect, useState } from "react";
import Modal from "react-modal";

import { sanitizeHtml } from "../../../../shared/sanitize";
import CloseIcon from "../../../../assets/icons/close.svg";
import moment from "moment";
import Axios from "../../../../services/Axios";
import APIVariables from "../../../../services/api-variables";
import InformationGuideRead from "../../../authentication/informationGuideRead";

const DrugRegistrationModal = ({
  isOpen,
  productConfirmations,
  profileDetails,
  handleCheckboxes,
  registerProduct,
  checkboxesMedications,
  user_type: userType,
  productID,
  products,
  onRequestClose,
  isValid,
  isregistering,
  setWasLinkClicked,
  wasLinkClicked,
  openGuideRead,
  closeModalGuideRead,
  confirmInformationGuideRead,
  handleCheckConfirmInformationGuideRead,
  clearData,
}) => {
  const customStyles = {
    content: {
      position: "absolute",
      top: "50%",
      left: "50%",
      right: "auto",
      bottom: "auto",
      marginRight: "-50%",
      transform: "translate(-50%, -50%)",
      width: "min(92vw, 720px)",
      minHeight: "50%",
      maxHeight: "85vh",
      padding: 0,
      border: "none",
      borderRadius: "10px",
      backgroundColor: "#ffffff",
      overflow: "hidden",
      boxShadow: "0px 0px 40px rgba(0, 0, 0, 0.2)",
    },
    overlay: {
      backgroundColor: "rgba(0, 0, 0, 0.2)",
      zIndex: 1000,
    },
  };

  const handleClose = () => {
    clearData();
    onRequestClose();
  };

  const [localProducts, setLocalProducts] = useState(products || []);

  const productObj = localProducts?.find((el) => el?.product_id === productID);
  const nameProduct = productObj?.name || "";

  const [brochureIdAndName, setBrochureIdAndName] = useState([]);

  const [registrationTexts, setRegistrationTexts] = useState({
    form_header: "",
    molecule_header: "",
    form_footer: "",
    raw: {},
  });

  const confirmationItems = Array.isArray(productConfirmations)
    ? productConfirmations
    : [];
  const registrationRole = userType === 1 ? "Prescriber" : "Pharmacy";


  useEffect(() => {
    const fetchRegistrationTexts = async () => {
      try {
        const resp = await Axios.post(
          APIVariables?.USER_REGISTRATION || "/settings/user-registration/",
          // Note: in this component `userType === 1` means "Prescriber" and 2 means all the other types.
          // The API expects the opposite mapping: 1 = Pharmacy, 0 = Prescriber.
          // Send the inverted value so the API receives the correct user_type.
          { user_type: userType === 1 ? 0 : 1 }
        );
        const data = resp?.data || {};
        setRegistrationTexts({
          form_header: data.first_confirmation || "",
          molecule_header: data.molecule_header || `Pharmacy {title} Confirmation`,
          form_footer: data.second_confirmation || "",
          raw: data,
        });
      } catch (err) {
      }
    };

    fetchRegistrationTexts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userType, productID]);

  useEffect(() => {
    const getLocalProducts = async () => {
      try {
        const res = await Axios.get(APIVariables.GET_PRODUCTS);
        if (res?.status === 200) {
          setLocalProducts(res.data);
        }
      } catch (error) {
        console.error("Failed to load products", error);
      }
    };

    try {
      getLocalProducts();
    } catch (error) {
      console.error("Failed to load products", error);
    }
  }, []);

  const interpolate = (tpl = "") => {
    if (!tpl) return "";
    return tpl
      .replace(/\{\{\s*PRODUCTS_LIST\s*\}\}/gi, nameProduct || "")
      .replace(/\{\{\s*INSTITUTION_NAME\s*\}\}/gi, profileDetails?.pharmacy?.value || "");
  };

  const renderMoleculeHeader = (tpl = "", title = "") => {
    const template = tpl || `${registrationRole} {title} Confirmation`;
    return template.replace(/\{?\{?title\}?\}?/gi, title);
  };

  useEffect(() => {
    const confirmationList = Array.isArray(productConfirmations)
      ? productConfirmations
      : [];

    const processedData = confirmationList
      .flat()
      .filter(
        (item) =>
          item.brochure_name !== null &&
          item.confirmation_name.includes("<brochure>")
      )
      .reduce((acc, item) => {
        const existingItem = acc.find(
          (x) => x.brochure_id === item.brochure_id
        );

        if (!existingItem) {
          acc.push({
            brochure_id: item.brochure_id,
            brochure_name: item.brochure_name,
          });
        } else {
          // Check if brochure_name is not already included
          if (!existingItem.brochure_name.includes(item.brochure_name)) {
            existingItem.brochure_name += `, ${item.brochure_name}`;
          }
        }

        return acc;
      }, []);

    // Set the state with the processed data
    setBrochureIdAndName(processedData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productConfirmations]);

  const getBrochure = async (confirmation, openInNewPage = false) => {
    const updatedArray = brochureIdAndName.filter(
      (item) => item.brochure_id !== confirmation.brochure_id
    );

    updatedArray.length === 0 && setWasLinkClicked(true);
    setBrochureIdAndName(updatedArray);

    await Axios.get(
      APIVariables.PATIENTS_BROCHURES_DOWNLOAD + confirmation.brochure_id
    )
      .then((res) => {
        const { data, status } = res;
        if (status === 200) {
          return data?.report;
        }
        return null;
      })
      .then(async (brochureString) => {
        const base64 = await fetch(
          `data:application/pdf;base64,${brochureString}`
        );
        return base64.blob();
      })
      .then((blob) => {
        const file = new Blob([blob], {
          type: "application/pdf",
        });
        const fileURL = URL.createObjectURL(file);
        const downloadLink = document.createElement("a");
        downloadLink.href = fileURL;
        if (openInNewPage) {
          downloadLink.target = "_blank";
        } else {
          downloadLink.download = confirmation.brochure_name + ".pdf";
        }
        downloadLink.click();
        downloadLink.remove();
        URL.revokeObjectURL(downloadLink.href);
      })
      .catch(() => { });
  };
  const handleBrochureClick = (brochureId) => {
    getBrochure(brochureId);
  };

  const productLevelText =
    userType === 1
      ? productObj?.prescriber_confirmation_text
      : productObj?.pharmacist_confirmation_text;

  const moleculeHeader = renderMoleculeHeader(
    registrationTexts.molecule_header,
    nameProduct
  );

  return (
    <Modal
      isOpen={isOpen}
      style={customStyles}
      overlayClassName="overlay-modal"
      shouldCloseOnOverlayClick={false}
      className="outline-none"
      contentLabel="Drug registration confirmation"
      data={{ testid: "profile-drug-registration-modal-dialog" }}
    >
      <div
        className="relative max-h-[85vh] overflow-y-auto px-6 py-5 text-left"
        data-testid="profile-drug-registration-modal"
      >
        <div className="mb-4 flex justify-end">
          <button
            type="button"
            className="rounded-full bg-[#9ABDB8] p-1"
            onClick={handleClose}
            aria-label="Close"
            data-testid="profile-drug-registration-modal-close"
          >
            <img
              src={CloseIcon}
              className="block h-5 w-5 brightness-0 invert"
              alt=""
            />
          </button>
        </div>

        {registrationTexts?.form_header && (
          <div
            className="mb-6 text-base text-neutral-700"
            data-testid="profile-drug-registration-form-header"
          >
            {interpolate(registrationTexts.form_header)}
          </div>
        )}

        <div data-testid="profile-drug-registration-confirmations">
          <h3
            className="mb-2 text-lg font-semibold text-[#00567a]"
            data-testid="profile-drug-registration-molecule-header"
          >
            {moleculeHeader}
          </h3>
          {productLevelText && (
            <p
              className="mb-8 text-base text-neutral-700"
              data-testid="profile-drug-registration-product-text"
            >
              {interpolate(productLevelText)}
            </p>
          )}

          {confirmationItems.map((confirmation, index) => {
            const displayName = (confirmation?.confirmation_name || "")
              .replace(/\r\n/g, "<br />")
              .replace(/<brochure>/g, "{brochure}");
            const parts = displayName.split("{brochure}");
            const clickId = confirmation?.confirmation_id;

            return (
              <div
                key={clickId}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    handleCheckboxes(clickId);
                  }
                }}
                onClick={() => {
                  handleCheckboxes(clickId);
                }}
                className="custom-checkbox my-4 flex cursor-pointer items-start gap-3"
                data-testid={`profile-drug-confirmation-row-${clickId}`}
              >
                <input
                  type="checkbox"
                  checked={checkboxesMedications.includes(clickId)}
                  readOnly
                  data-testid={`profile-drug-confirmation-checkbox-${clickId}`}
                />
                <span className="checkmark mt-0.5 shrink-0" />
                <strong data-testid={`profile-drug-confirmation-number-${clickId}`}>
                  {index + 1}.
                </strong>
                <div data-testid={`profile-drug-confirmation-label-${clickId}`}>
                  {parts.map((part, partIndex) => (
                    <React.Fragment key={`part_${clickId}_${partIndex}`}>
                      <span dangerouslySetInnerHTML={sanitizeHtml(part)} />
                      {partIndex < parts.length - 1 && (
                        <span
                          key={`brochure_${clickId}`}
                          className="brochure-link"
                          data-testid={`profile-drug-brochure-link-${confirmation?.brochure_id}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            handleBrochureClick(confirmation);
                          }}
                        >
                          {confirmation?.brochure_name}
                        </span>
                      )}
                    </React.Fragment>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        {registrationTexts?.form_footer && (
          <div
            className="mb-6 text-base text-neutral-700"
            data-testid="profile-drug-registration-form-footer"
          >
            {interpolate(registrationTexts.form_footer)}
          </div>
        )}

        <div
          className="mb-6 grid gap-6 md:grid-cols-2"
          data-testid="profile-drug-registration-signature"
        >
          <div className="flex flex-col">
            <label
              className="mb-1 text-[#00567a]"
              data-testid="profile-drug-prescriber-name-label"
            >
              Prescriber Name
            </label>
            <input
              type="text"
              className="mb-4 border-0 border-b border-neutral-400 bg-white text-lg text-neutral-600 outline-none"
              name="prescriberName"
              value={profileDetails?.first_name + " " + profileDetails?.last_name}
              disabled
              data-testid="profile-drug-prescriber-name-input"
            />
          </div>
          <div className="flex flex-col">
            <label
              className="mb-1 text-[#00567a]"
              data-testid="profile-drug-date-label"
            >
              Date
            </label>
            <input
              className="mb-4 border-0 border-b border-neutral-400 bg-white text-lg text-neutral-600 outline-none"
              value={moment().format("DD / MM / YYYY")}
              disabled
              readOnly
              data-testid="profile-drug-date-input"
            />
          </div>
        </div>

        <div
          className="mb-2 flex flex-wrap items-center justify-between gap-4"
          data-testid="profile-drug-registration-actions"
        >
          <button
            type="button"
            className="border-0 bg-transparent text-[#00567a] underline"
            onClick={handleClose}
            data-testid="profile-drug-previous-button"
          >
            Previous
          </button>
          <button
            type="button"
            className="h-11.5 min-w-40 rounded-full border-0 bg-[#00567a] px-6 text-lg font-bold text-white hover:bg-[#005eb8] disabled:bg-neutral-400"
            disabled={
              confirmationItems.length !== 0
                ? isregistering === true &&
                  isValid === true &&
                  Boolean(brochureIdAndName.length)
                : true
            }
            onClick={() => registerProduct()}
            data-testid="profile-drug-register-button"
          >
            Register
          </button>
        </div>

        <div
          className="min-h-12.5 text-sm text-[#fb3449]"
          data-testid="profile-drug-registration-errors"
        >
          {isregistering === true && isValid === false ? (
            <p data-testid="profile-drug-registration-agreement-error">
              The {userType === 1 ? "prescriber" : "pharmacy"} has not
              agreed to 1 or more statements on this page.
              <br />
              Please tick the boxes and confirm that the patient has agreed
              before continuing
            </p>
          ) : null}

          {isregistering === true &&
            isValid === true &&
            Boolean(brochureIdAndName.length) &&
            wasLinkClicked === false && (
              <p data-testid="profile-drug-registration-brochure-error">
                Please read{" "}
                {brochureIdAndName.map((element) => (
                  <span
                    key={element.brochure_id}
                    className="block"
                    data-testid={`profile-drug-registration-unread-brochure-${element.brochure_id}`}
                  >
                    {element.brochure_name}
                  </span>
                ))}
                Once read, click Register to continue
              </p>
            )}
        </div>

        <InformationGuideRead
          openGuideRead={openGuideRead}
          closeModalGuideRead={closeModalGuideRead}
          confirmInformationGuideRead={confirmInformationGuideRead}
          handleCheckConfirmInformationGuideRead={
            handleCheckConfirmInformationGuideRead
          }
        />
      </div>
    </Modal>
  );
};

export default DrugRegistrationModal;
